import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Spin,
  message,
} from 'antd';
import axios from 'axios';
import csrfHeaders from '../../utils/csrfHeaders';
import ReferralDetail from '../../containers/portal/ReferralDetail';

const ShareReferralDrawer = ({
  visible, fileToken, cable, onClose, onRevoked, onActivityRead, afterVisibleChange,
}) => {
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [closingVariant, setClosingVariant] = useState(null);
  const previousVisibleRef = useRef(visible);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/records/${fileToken}/shares/detail`);
      setReferral(response.data.referral);
    } catch {
      message.error({ content: 'Failed to load referral details' });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [fileToken, onClose]);

  // Hide Intercom launcher while the drawer is open so it doesn't cover the send button
  useEffect(() => {
    if (visible && window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
    return () => {
      if (window.Intercom) {
        window.Intercom('update', { hide_default_launcher: false });
      }
    };
  }, [visible]);

  useEffect(() => {
    if (visible && fileToken) {
      fetchDetail();
    }
  }, [visible, fileToken, fetchDetail]);

  const isLoadingVariant = loading || !referral;

  useEffect(() => {
    if (previousVisibleRef.current && !visible) {
      setClosingVariant(isLoadingVariant ? 'loading' : 'detail');
    }

    if (!previousVisibleRef.current && visible) {
      setClosingVariant(null);
    }

    previousVisibleRef.current = visible;
  }, [visible, isLoadingVariant]);

  const handleAfterVisibleChange = (isOpen) => {
    if (!isOpen) {
      setReferral(null);
      setClosingVariant(null);
    }

    if (afterVisibleChange) {
      afterVisibleChange(isOpen);
    }
  };

  const handleRevoke = async () => {
    const shareId = referral?.shared_with?.share_id;
    if (!shareId) return;

    setRevoking(true);
    try {
      await axios.delete(`/api/v1/records/${fileToken}/shares/${shareId}`, {
        headers: csrfHeaders(),
      });
      message.success({ content: 'Share access revoked successfully' });
      if (onRevoked) {
        onRevoked();
      } else {
        onClose();
      }
    } catch {
      message.error({ content: 'Failed to revoke access' });
    } finally {
      setRevoking(false);
    }
  };

  let drawerVariant = isLoadingVariant ? 'loading' : 'detail';

  if (!visible && closingVariant) {
    drawerVariant = closingVariant;
  }

  if (drawerVariant === 'loading') {
    const loadingStyle = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    };

    return (
      <Drawer
        placement="right"
        visible={visible}
        onClose={onClose}
        width={680}
        closable={false}
        bodyStyle={{ padding: 0 }}
        mask
        maskClosable
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
        className="referral-detail-drawer"
        afterVisibleChange={handleAfterVisibleChange}
      >
        <div style={loadingStyle}>
          <Spin size="large" />
        </div>
      </Drawer>
    );
  }

  return (
    <ReferralDetail
      referral={referral}
      visible={visible}
      onClose={onClose}
      cable={cable}
      sharedWith={referral.shared_with}
      onRevoke={handleRevoke}
      revoking={revoking}
      onActivityRead={onActivityRead}
      afterVisibleChange={handleAfterVisibleChange}
    />
  );
};

ShareReferralDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  fileToken: PropTypes.string.isRequired,
  cable: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onRevoked: PropTypes.func,
  onActivityRead: PropTypes.func,
  afterVisibleChange: PropTypes.func,
};

ShareReferralDrawer.defaultProps = {
  cable: null,
  onRevoked: null,
  onActivityRead: null,
  afterVisibleChange: null,
};

export default ShareReferralDrawer;
